<template>
  <div>
    <div v-if="student" class="font-weight-bold">
      {{ student.firstName }} {{ student.lastName }}
    </div>

    <div class="justify-space-between d-flex my-4">
      <div v-for="(count, status) in statusCounts" :key="status">
        <span class="font-weight-bold"> {{ status }}:</span> {{ count }}
      </div>
    </div>

    <date-range-slider
      v-if="minDate && maxDate"
      v-model="dateRange"
      :minDate="minDate"
      :maxDate="maxDate"
    />

    <v-select
      v-if="programs && programs.length > 1"
      label="Filter by Programs"
      :items="programs"
      v-model="selectedPrograms"
      item-value="id"
      item-text="name"
      multiple
    ></v-select>

    <v-data-table
      :headers="headers"
      :items="filteredAttendances"
      class="elevation-1"
    >
      <template v-slot:item.attendedDate="{ item }">
        {{ new Date(item.attendedDate).toLocaleDateString() }}
      </template>
      <template v-slot:item.status="{ item }">
        {{ item.status }}
        <v-tooltip v-if="item.notes" bottom>
          <template v-slot:activator="{ on }">
            <v-icon class="ml-2 my-auto" v-on="on">mdi-note-outline</v-icon>
          </template>
          <template v-slot:default>
            <span>{{ item.notes }}</span>
          </template>
        </v-tooltip>
      </template>
      <template v-slot:item.programId="{ item }">
        {{ getProgramName(item.programId) }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import AttendanceService from "@/services/attendanceService.js";
import DateRangeSlider from "@/components/common/DateRangeSlider.vue";

export default {
  components: { DateRangeSlider },
  props: {
    value: {
      type: Object,
      required: true,
    },
    programIds: {
      type: Array,
      required: false,
    },
  },
  computed: {
    statusCounts() {
      return this.filteredAttendances.reduce((counts, attendance) => {
        if (counts[attendance.status]) {
          counts[attendance.status]++;
        } else {
          counts[attendance.status] = 1;
        }
        return counts;
      }, {});
    },
    dateDifferenceInDays() {
      return (this.maxDate - this.minDate) / (1000 * 60 * 60 * 24);
    },
    filteredAttendances() {
      const startTime =
        this.dateRange && this.dateRange[0]
          ? this.dateRange[0].getTime()
          : Number.MIN_SAFE_INTEGER;
      const endTime =
        this.dateRange && this.dateRange[1]
          ? this.dateRange[1].getTime()
          : Number.MAX_SAFE_INTEGER;

      return this.attendances.filter((a) => {
        const attendedTime = new Date(a.attendedDate).getTime();
        const isWithinDateRange =
          attendedTime >= startTime && attendedTime <= endTime;
        const isSelectedProgram = this.selectedPrograms.includes(a.programId);

        return (
          isWithinDateRange &&
          (this.selectedPrograms.length === 0 || isSelectedProgram)
        );
      });
    },
  },
  data: () => ({
    student: null,
    attendances: [],
    dateRange: [],
    minDate: null,
    maxDate: null,
    programs: [],
    selectedPrograms: [],
    headers: [
      { text: "Attended Date", value: "attendedDate" },
      { text: "Status", value: "status" },
      { text: "Program", value: "programId" },
    ],
  }),
  watch: {
    student(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchAttendance(newVal);
      }
    },
  },
  mounted() {
    this.student = this.value;
    if (this.programIds && this.programIds.length > 0) {
      this.selectedPrograms = this.programIds;
    }
  },
  methods: {
    async fetchAttendance(student) {
      // Fetch attendances
      const studentIds = [student.id];
      this.attendances = await AttendanceService.findByStudentIds(studentIds);
      //sort attendance on date desc
      this.attendances.sort((a, b) => {
        const dateA = a.attendedDate;
        const dateB = b.attendedDate;

        if (dateA > dateB) {
          return -1;
        }
        if (dateA < dateB) {
          return 1;
        }
        return 0;
      });

      this.filteredAttendances = this.attendances;

      this.minDate = Math.min(
        ...this.attendances.map((a) => new Date(a.attendedDate).getTime())
      );
      this.maxDate = Math.max(
        ...this.attendances.map((a) => new Date(a.attendedDate).getTime())
      );

      // Add a day before and a day after to minDate and maxDate
      this.minDate -= 86400000; // Subtract 1 day in milliseconds
      this.maxDate += 86400000; // Add 1 day in milliseconds

      // Assuming the programId in the records is a string or number
      const programIds = [...new Set(this.attendances.map((a) => a.programId))];

      this.programs = await this.loadPrograms(
        this.student.centerId,
        programIds
      );
    },

    async loadPrograms(center, programIds) {
      const params = { centerId: center };
      return await this.$axios
        .get("/program/get-by-center", { params })
        .then((resp) => {
          const programList = resp.data;
          return programList.filter((p) => programIds.includes(p.id));
        });
    },
    getProgramName(id) {
      if (this.programs) {
        const program = this.programs.find((p) => p.id == id);
        return program ? program.name : "N/A";
      } else {
        return "N/A";
      }
    },
  },
};
</script>

<style>
</style>
